import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, COUNTRY_FLAGS, COUNTRY_LABELS } from "@/lib/api";
import {
  Users, Search, UserX, UserCheck, BadgeCheck, Shield, Filter,
  ChevronLeft, ChevronRight, AlertTriangle, Home,
} from "lucide-react";
import { toast } from "sonner";

const ROLE_COLORS = {
  particulier: "#9a9a9a",
  pro: "#11783A",
  admin: "#D6202C",
};

const PAGE_SIZE = 25;

export default function AdminUsers() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [bannedFilter, setBannedFilter] = useState(""); // "" | "yes" | "no"
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ total: 0, items: [] });
  const [counts, setCounts] = useState({ all: 0, by_role: {}, banned: 0 });
  const [confirm, setConfirm] = useState(null); // {user, action: 'ban'|'unban'|'role', newRole?}
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) return navigate("/login");
    if (user.role !== "admin") return navigate("/dashboard");
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, q, roleFilter, bannedFilter, page]);

  const refresh = async () => {
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (q) params.q = q;
      if (roleFilter) params.role = roleFilter;
      if (bannedFilter) params.banned = bannedFilter === "yes";
      const [{ data: list }, { data: c }] = await Promise.all([
        api.get("/admin/users", { params }),
        api.get("/admin/users/counts"),
      ]);
      setData(list);
      setCounts(c);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const performAction = async () => {
    if (!confirm) return;
    setBusyId(confirm.user.id);
    try {
      const body = {};
      if (confirm.action === "ban") body.banned = true;
      else if (confirm.action === "unban") body.banned = false;
      else if (confirm.action === "role") body.role = confirm.newRole;
      await api.patch(`/admin/users/${confirm.user.id}`, body);
      toast.success("Utilisateur mis à jour");
      setConfirm(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  if (!user || user.role !== "admin") return null;

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <Layout withFooter={false}>
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8" data-testid="admin-users">
        <span className="text-xs tracking-[0.2em] uppercase text-[#D6202C] font-medium">Admin · Utilisateurs</span>
        <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight mt-2 mb-2 flex items-center gap-3">
          Utilisateurs <Users className="w-8 h-8 text-[#D6202C]" />
        </h1>
        <p className="text-muted-foreground mb-8">
          {counts.all} comptes · {counts.by_role.pro || 0} Pro · {counts.by_role.particulier || 0} Particuliers · {counts.banned} bannis
        </p>

        {/* Filters bar */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(0); }}
                placeholder="Email, nom, société..."
                data-testid="users-search-input"
                className="pl-10 rounded-xl"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}
              data-testid="users-role-filter"
              className="h-10 px-3 rounded-xl border border-[#E5E5E5] text-sm bg-white"
            >
              <option value="">Tous les rôles</option>
              <option value="particulier">Particulier</option>
              <option value="pro">Pro</option>
              <option value="admin">Admin</option>
            </select>
            <select
              value={bannedFilter}
              onChange={(e) => { setBannedFilter(e.target.value); setPage(0); }}
              data-testid="users-banned-filter"
              className="h-10 px-3 rounded-xl border border-[#E5E5E5] text-sm bg-white"
            >
              <option value="">Statut : tous</option>
              <option value="no">Actifs</option>
              <option value="yes">Bannis</option>
            </select>
          </div>
        </div>

        {data.items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#E5E5E5] p-12 text-center" data-testid="users-empty">
            <Users className="w-10 h-10 text-[#E5E5E5] mx-auto mb-3" />
            <p className="text-muted-foreground">Aucun utilisateur ne correspond aux filtres.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden" data-testid="users-table">
            <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 bg-[#FAFAFA] border-b border-[#E5E5E5] text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
              <div className="col-span-4">Utilisateur</div>
              <div className="col-span-2">Rôle / Pays</div>
              <div className="col-span-2">Annonces</div>
              <div className="col-span-2">Inscrit</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {data.items.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                isCurrent={u.id === user.id}
                busy={busyId === u.id}
                onAction={(action, newRole) => setConfirm({ user: u, action, newRole })}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {data.total > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4" data-testid="users-pagination">
            <div className="text-xs text-muted-foreground">
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, data.total)} sur {data.total}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                data-testid="users-prev-page"
                className="rounded-xl"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-3 py-1.5 text-sm">{page + 1} / {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage(p => p + 1)}
                data-testid="users-next-page"
                className="rounded-xl"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {confirm && (
        <ConfirmModal
          confirm={confirm}
          onCancel={() => setConfirm(null)}
          onConfirm={performAction}
        />
      )}
    </Layout>
  );
}

const UserRow = ({ user, isCurrent, busy, onAction }) => {
  const banned = !!user.banned;
  return (
    <div
      className={`grid grid-cols-1 md:grid-cols-12 gap-3 px-4 py-4 border-b border-[#F5F0EB] hover:bg-[#FAFAFA] transition text-sm ${banned ? "opacity-60" : ""}`}
      data-testid={`user-row-${user.id}`}
    >
      <div className="md:col-span-4 flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-full bg-[#11783A] text-white flex items-center justify-center font-medium shrink-0">
          {(user.name || "?").charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="font-medium truncate flex items-center gap-1">
            {user.name}
            {user.badge === "pro_verified" && <BadgeCheck className="w-3.5 h-3.5 text-[#11783A]" />}
            {isCurrent && <span className="text-[10px] text-muted-foreground">(vous)</span>}
          </div>
          <div className="text-[11px] text-muted-foreground truncate">
            {user.email} {user.company ? `· ${user.company}` : ""}
          </div>
        </div>
      </div>

      <div className="md:col-span-2 flex items-center gap-1.5 flex-wrap">
        <span
          className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium"
          style={{ background: `${ROLE_COLORS[user.role]}15`, color: ROLE_COLORS[user.role] }}
        >
          {user.role}
        </span>
        {user.country && <span className="text-xs">{COUNTRY_FLAGS[user.country]} {user.country}</span>}
        {banned && (
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#7a0a12]/15 text-[#7a0a12] font-medium">
            Banni
          </span>
        )}
      </div>

      <div className="md:col-span-2 flex items-center gap-1 text-muted-foreground">
        <Home className="w-3.5 h-3.5" /> {user.listings_count || 0}
      </div>

      <div className="md:col-span-2 text-muted-foreground text-xs">
        {user.created_at ? new Date(user.created_at).toLocaleDateString("fr-FR") : ""}
      </div>

      <div className="md:col-span-2 flex md:justify-end gap-2 flex-wrap">
        {user.role === "pro" && (
          <Link
            to={`/agence/${user.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] underline text-muted-foreground hover:text-[#D6202C] self-center"
          >
            Voir agence
          </Link>
        )}
        {!isCurrent && (
          banned ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAction("unban")}
              disabled={busy}
              data-testid={`user-unban-${user.id}`}
              className="rounded-xl text-xs h-8 px-3 border-[#11783A]/30 text-[#11783A]"
            >
              <UserCheck className="w-3.5 h-3.5 mr-1" /> Débannir
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAction("ban")}
              disabled={busy}
              data-testid={`user-ban-${user.id}`}
              className="rounded-xl text-xs h-8 px-3 border-[#D6202C]/30 text-[#D6202C]"
            >
              <UserX className="w-3.5 h-3.5 mr-1" /> Bannir
            </Button>
          )
        )}
      </div>
    </div>
  );
};

const ConfirmModal = ({ confirm, onCancel, onConfirm }) => {
  const isBan = confirm.action === "ban";
  const isUnban = confirm.action === "unban";
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onCancel} data-testid="users-confirm-modal">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
          style={{
            background: isBan ? "#D6202C15" : isUnban ? "#11783A15" : "#9a9a9a15",
            color: isBan ? "#D6202C" : isUnban ? "#11783A" : "#9a9a9a",
          }}
        >
          {isBan ? <UserX className="w-6 h-6" /> : isUnban ? <UserCheck className="w-6 h-6" /> : <Shield className="w-6 h-6" />}
        </div>
        <h3 className="font-heading text-xl font-medium mb-2">
          {isBan ? "Bannir cet utilisateur ?" : isUnban ? "Débannir cet utilisateur ?" : "Changer le rôle ?"}
        </h3>
        <p className="text-sm text-muted-foreground mb-1">
          <strong>{confirm.user.name}</strong> ({confirm.user.email})
        </p>
        <p className="text-xs text-muted-foreground mb-6">
          {isBan
            ? "L'utilisateur ne pourra plus se connecter et toutes ses annonces actives seront cachées (réversible)."
            : isUnban
            ? "L'utilisateur pourra à nouveau se connecter et ses annonces cachées par le bannissement seront restaurées."
            : ""}
        </p>
        {isBan && (
          <div className="bg-[#FEF2F3] rounded-xl p-3 mb-4 text-xs flex gap-2">
            <AlertTriangle className="w-4 h-4 text-[#D6202C] shrink-0 mt-0.5" />
            <span>Action réversible : le débannissement restaure le compte et ses annonces.</span>
          </div>
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} className="flex-1 rounded-xl">Annuler</Button>
          <Button
            onClick={onConfirm}
            data-testid="users-confirm-btn"
            className="flex-1 rounded-xl text-white"
            style={{ background: isBan ? "#D6202C" : isUnban ? "#11783A" : "#9a9a9a" }}
          >
            Confirmer
          </Button>
        </div>
      </div>
    </div>
  );
};
